import React from 'react';
import { createPortal } from 'react-dom';
import { pdf } from '@react-pdf/renderer';
import { Document as PDFDocument, Page as PDFPage, pdfjs } from 'react-pdf';
import RemnantBookPDF, { chapterWord } from '../RemnantBookPDF';
import ReadingCard from './ReadingCard';
import { buildReadingModel } from '../utils/revealPageText';
import { paginateBlocks } from '../utils/paginateToScreen';

/**
 * RevealPages — REVEAL-001 C2, the reveal's page viewer.
 *
 * Renders ONE chapter's BODY through the REAL RemnantBookPDF path (revealMode) — the same engine
 * that prints — and shows it as pages with a pager. Body words derive from that render (hard req
 * #1); only the title block's PRESENTATION is composed here.
 *
 * THREE MODES, and which one runs is decided by what the caller can give it:
 *
 *   PAGED (founder ruling 2026-08-10, option A — "real pages: each page exactly fills the screen,
 *     pager flips them, text never scrolls or hides"). Given a `fitHeight`, every print page's text
 *     is extracted up front, regrouped into ONE block list, and re-paginated into SCREEN pages that
 *     fit that height exactly. `paginateToScreen.js` owns the splitting and is gated for fidelity;
 *     measurement CLONES the really-rendered nodes, so there is no second copy of the typography to
 *     drift from the first. Mobile can also swipe.
 *   SCROLL CARD (no `fitHeight`, or a viewport too short for a real page — a phone in landscape).
 *     Today's behaviour: one PRINT page reflowed into a scrolling card. Worse than paged, never wrong.
 *   PRINT-EXACT CANVAS (`USE_CARD_ALL_WIDTHS = false`). The pdfjs canvas page, kept one constant
 *     away since the 2026-08-10 ruling that its ~10px on-screen type was unreadable, so restoring
 *     bigger-paper instead of reflow is a one-line change rather than an archaeology exercise.
 *
 * WHAT PAGED MODE DOES NOT CHANGE: the words are still the print render's, and the PRINTED book
 * keeps its own pagination untouched — "Page 3 of 9" on a phone and "page 2" in the hardcover are
 * both true, of different objects. `revealReadingCardParity.test.js` still gates the print path (it
 * drives `runs`, which paged mode leaves alone).
 *
 * Never blank: a calm loading state holds until the pages are ready, and `loadingFallback` lets a
 * streamed arrival keep showing the very card the teller was already reading.
 */

/** Below this a "page" would be a handful of lines — scroll instead of pretending to paginate. */
const MIN_PAGED_HEIGHT = 260;
/** A swipe must be mostly horizontal and past this to count as a page turn. */
const SWIPE_MIN_PX = 45;

export default function RevealPages({
  chapter,
  revealChapterNumber,
  chapterTitle,
  attribution,
  bookTitle,
  author,
  bookKey,
  onPageTurn,
  onNumPages,
  onReady, // REVEAL-002 R3/B2a: fires when the page has actually PAINTED (or on error) — so the
           // landing veil lifts INTO the page, never onto "Preparing your pages…".
  loadingFallback, // F-415-STREAM: rendered instead of "Preparing your pages…" while the pages
                   // generate — the streamed story card carries the teller across the seam.
  hidePager, // the reveal renders paging in its pinned dock instead
  onPagerState, // reports { page, total, go } up for that dock
  fitHeight, // px the page may occupy — the paged reader's stage. Absent → scroll card.
  expandable, // mobile: tapping the page opens the full-screen reader (founder idea 2026-08-10)
}) {
  const bookDoc = (
    <RemnantBookPDF chapters={[chapter]} revealMode revealChapterNumber={revealChapterNumber} title={bookTitle} author={author} />
  );

  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const prevKeyRef = React.useRef(null);

  React.useEffect(() => {
    if (bookKey && prevKeyRef.current === bookKey) return;
    prevKeyRef.current = bookKey || null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const blob = await pdf(bookDoc).toBlob();
        if (cancelled) return;
        setPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      } catch (err) {
        console.error('[RevealPages] PDF generation error:', err);
        if (!cancelled) setError('Something went wrong setting your chapter on the page. Please try again.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; prevKeyRef.current = null; };
  }, [bookKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [numPages, setNumPages] = React.useState(null);
  const [pdfDoc, setPdfDoc] = React.useState(null); // STATE (not ref) so the extract effect re-runs on a new doc
  const [pageIdx, setPageIdx] = React.useState(0);
  const [runs, setRuns] = React.useState(null); // scroll-card mode: ONE print page's runs
  const [allBlocks, setAllBlocks] = React.useState(null); // paged mode: the whole chapter
  const [screenPages, setScreenPages] = React.useState(null); // paged mode: blocks per screen page

  // Parse the blob via pdfjs (the proven path — same as BookBuilderPage) to get numPages + a doc for
  // text extraction. NOT a headless react-pdf <Document> (with no <Page> child it never fires
  // onLoadSuccess — the "Preparing your pages…" stuck-blank bug).
  React.useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;
    setPdfDoc(null);
    (async () => {
      try {
        const doc = await pdfjs.getDocument(pdfUrl).promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      } catch (err) {
        console.error('[RevealPages] pdfjs parse error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerRef = React.useRef(null);
  const measureHostRef = React.useRef(null);
  const [pageWidth, setPageWidth] = React.useState(null);
  const [cardWidth, setCardWidth] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const calc = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      setIsMobile(w <= 600);
      const maxFromH = (window.innerHeight - 240) * (8.5 / 11);
      // F-64: cap the rendered page at the reveal-body's design max (parent maxWidth 566, minus its
      // 1px border each side = 564 inner), or a tall window renders the canvas wider than its
      // container and overflow:hidden clips the words. Print geometry need not grow with a 4K monitor.
      const PAGE_MAX = 564;
      setPageWidth(Math.max(260, Math.min(w - 8, maxFromH, PAGE_MAX)));
      setCardWidth(Math.max(260, Math.min(w, 566)));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // ── THE FULL-SCREEN READER (founder idea 2026-08-10: "they just click the page and it opens a
  // reading panel that takes up most of the screen — that is the kindle experience") ─────────────
  //
  // WHY IT IS THE ONLY FIX THAT WORKS. Words per page is physics, not layout: at the constitutional
  // 16px a 312px page on a phone holds ~51 words, which turned a real telling into 26 pages. The
  // page cannot hold more unless the page gets BIGGER, and the only place bigger exists on a phone
  // is the whole screen. Measured, not assumed — see the numbers in the session ledger.
  //
  // It is the SAME component, blocks and measurer: only the stage changes, so opening the reader
  // cannot show different words from the page behind it.
  const [expanded, setExpanded] = React.useState(false);
  const [panelHeight, setPanelHeight] = React.useState(0);
  const panelStageRef = React.useRef(null);

  // Founder ruling 2026-08-10: the card at every width (the print-exact canvas put ~10px type on a
  // desktop screen). The canvas branch stays reachable by flipping this one constant.
  const USE_CARD_ALL_WIDTHS = true;
  const useCard = USE_CARD_ALL_WIDTHS || isMobile;
  const stageHeight = expanded ? panelHeight : fitHeight;
  const paged = useCard && Number.isFinite(stageHeight) && stageHeight >= MIN_PAGED_HEIGHT;

  React.useLayoutEffect(() => {
    if (!expanded) { setPanelHeight(0); return undefined; }
    const measure = () => {
      const el = panelStageRef.current;
      if (el) setPanelHeight((prev) => { const h = Math.floor(el.clientHeight); return Math.abs(prev - h) > 4 ? h : prev; });
    };
    measure();
    let frames = 0, raf = requestAnimationFrame(function settle() { measure(); if (++frames < 8) raf = requestAnimationFrame(settle); });
    window.addEventListener('resize', measure);
    // The page behind must not scroll under the reader.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  // Reset paging + extracted text whenever the document is regenerated.
  React.useEffect(() => { setPageIdx(0); setRuns(null); setAllBlocks(null); setScreenPages(null); }, [bookKey]);

  const extractRuns = React.useCallback(async (proxy, oneBasedPage) => {
    try {
      const page = await proxy.getPage(oneBasedPage);
      const tc = await page.getTextContent();
      return tc.items
        .filter((it) => it.str && it.str.trim())
        .map((it) => ({ str: it.str, x: it.transform[4], y: it.transform[5], fontSize: Math.hypot(it.transform[0], it.transform[1]), width: it.width }));
    } catch { return []; }
  }, []);

  // ── SCROLL-CARD mode: one print page at a time ──────────────────────────────────────────────
  React.useEffect(() => {
    if (paged || !useCard || !pdfDoc) return;
    let cancelled = false;
    // Do NOT null the runs here: clearing them collapsed the card to the loading block for a beat
    // on every page turn, and that layout jump read as a scroll-to-top (walk round 2).
    extractRuns(pdfDoc, pageIdx + 1).then((r) => { if (!cancelled) setRuns(r); });
    return () => { cancelled = true; };
  }, [paged, useCard, pageIdx, pdfDoc, extractRuns]);

  // ── PAGED mode, step 1: the WHOLE chapter's blocks, from the real print render ───────────────
  const kicker = `Chapter ${chapterWord(revealChapterNumber || 1)}`;
  React.useEffect(() => {
    if (!paged || !pdfDoc || !numPages) return;
    let cancelled = false;
    (async () => {
      const blocks = [];
      for (let p = 1; p <= numPages; p++) {
        const pageRuns = await extractRuns(pdfDoc, p);
        if (cancelled) return;
        blocks.push(...buildReadingModel(pageRuns).blocks);
      }
      // The chapter opening is COMPOSED, exactly as it was when it sat above the card — print
      // renders the body only. In paged mode it belongs INSIDE page one, where a book keeps it.
      const opening = [{ kind: 'eyebrow', text: kicker }, { kind: 'heading', text: chapterTitle || '' }];
      if (attribution) opening.push({ kind: 'eyebrow', text: attribution });
      if (!cancelled) setAllBlocks(opening.filter((b) => b.text).concat(blocks));
    })();
    return () => { cancelled = true; };
  }, [paged, pdfDoc, numPages, extractRuns, kicker, chapterTitle, attribution]);

  // ── PAGED mode, step 2: re-paginate to the stage, measuring the REAL rendered nodes ──────────
  //
  // The hidden host below renders the actual ReadingCard with every block. Measuring clones its
  // nodes, so the measurer cannot disagree with the renderer about typography — the failure a
  // character-width estimate would reintroduce.
  React.useEffect(() => {
    if (!paged || !allBlocks || !allBlocks.length || !measureHostRef.current || !cardWidth) return;
    const host = measureHostRef.current;
    const realCard = host.firstElementChild;
    if (!realCard) return;

    const templates = {};
    Array.from(realCard.children).forEach((el, i) => {
      const kind = (allBlocks[i] && allBlocks[i].kind) || 'body';
      if (!templates[kind]) templates[kind] = el;
    });
    if (!templates.body) return; // nothing measurable yet — the card has not painted

    const scratch = realCard.cloneNode(false);
    scratch.style.height = 'auto';
    scratch.style.width = `${cardWidth}px`;
    host.appendChild(scratch);

    const cache = new Map();
    const measure = (blocks) => {
      const key = JSON.stringify(blocks.map((b) => [b.kind, b.text]));
      const hit = cache.get(key);
      if (hit !== undefined) return hit;
      scratch.textContent = '';
      for (const b of blocks) {
        const el = (templates[b.kind] || templates.body).cloneNode(false);
        el.textContent = b.text;
        scratch.appendChild(el);
      }
      const h = scratch.offsetHeight;
      cache.set(key, h);
      return h;
    };

    let result;
    try {
      result = paginateBlocks({ blocks: allBlocks, measure, pageHeight: stageHeight });
    } finally {
      host.removeChild(scratch);
    }
    const pages = result.pages.length ? result.pages : [allBlocks];
    // Opening or closing the reader re-paginates to a different stage, so the OLD page number means
    // nothing. Keep the reader's PLACE instead: find the page holding the word they were on.
    const wordAt = (list, idx) => list.slice(0, idx).reduce((n, pg) => n + pg.reduce((m, b) => m + b.text.split(/\s+/).length, 0), 0);
    const target = screenPages ? wordAt(screenPages, pageIdx) : 0;
    let acc = 0, landed = 0;
    for (let i = 0; i < pages.length; i++) {
      const words = pages[i].reduce((m, b) => m + b.text.split(/\s+/).length, 0);
      if (target < acc + words) { landed = i; break; }
      acc += words;
      landed = i;
    }
    setScreenPages(pages);
    setPageIdx(landed);
  }, [paged, allBlocks, stageHeight, cardWidth]);

  const total = paged ? (screenPages ? screenPages.length : 1) : (numPages || 1);
  React.useEffect(() => { if (onNumPages) onNumPages(total); }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  const atStart = pageIdx === 0;
  const atEnd = pageIdx >= total - 1;
  const go = (delta) => {
    const next = Math.max(0, Math.min(total - 1, pageIdx + delta));
    if (next === pageIdx) return;
    setPageIdx(next);
    if (onPageTurn) onPageTurn(next + 1);
    // PAGED mode never scrolls — the page is already wholly on screen, which is the ruling itself.
    // The scroll card still needs to land the reader at the page top, clear of the fixed header.
    if (!paged && containerRef.current) {
      const y = containerRef.current.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  };

  // Hand the dock live paging state.
  React.useEffect(() => {
    if (!onPagerState) return;
    onPagerState({ page: pageIdx + 1, total, go });
  }, [pageIdx, total]); // eslint-disable-line react-hooks/exhaustive-deps

  // Swipe to turn, mobile only — the buttons stay for anyone who would rather tap.
  const touchRef = React.useRef(null);
  const onTouchStart = (e) => {
    if (!paged || (!isMobile && !expanded) || e.touches.length !== 1) return;
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start || !e.changedTouches || !e.changedTouches.length) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return; // a scroll, not a turn
    go(dx < 0 ? 1 : -1);
  };

  const blobReady = !loading && !error && pageWidth && pdfUrl;
  const bodyReady = paged
    ? (blobReady && screenPages !== null)
    : (blobReady && (!useCard || (numPages && runs !== null)));

  React.useEffect(() => { if (onReady && useCard && bodyReady) onReady(); }, [onReady, useCard, bodyReady]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (onReady && error) onReady(); }, [onReady, error]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageBlocks = paged && screenPages ? (screenPages[pageIdx] || screenPages[0] || []) : null;
  // Only offer the reader where the page is genuinely cramped. On a desktop the inline page already
  // holds ~142 words, so a modal would be ceremony rather than help.
  const canExpand = !!expandable && isMobile && paged && !expanded && bodyReady;

  return (
    <div ref={containerRef} data-testid="reveal-pages"
      style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', ...(paged ? { height: '100%', minHeight: 0 } : null) }}>
      {error ? (
        <div style={{ padding: '40px 16px', color: '#c0392b', textAlign: 'center', fontFamily: "'Lora', serif" }}>{error}</div>
      ) : (
        <>
          {/* Composed title block — SCROLL-CARD mode only. In paged mode the opening rides inside
              page one (see the opening blocks above), which is where a book keeps it. */}
          {!paged && pageIdx === 0 && (
            <div data-testid="reveal-title-block" style={{ textAlign: 'center', maxWidth: 566, width: '100%', margin: '4px auto 14px', fontFamily: "'Lora', serif", color: '#2a2520' }}>
              <div data-testid="reveal-kicker" style={{ fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#5a524a' }}>{kicker}</div>
              <div data-testid="reveal-title" style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3, marginTop: 10 }}>{chapterTitle}</div>
              {attribution ? <div style={{ fontStyle: 'italic', fontSize: 13, color: '#5a524a', marginTop: 8 }}>{attribution}</div> : null}
              <div style={{ width: 40, height: 1, background: '#d4c4a8', margin: '16px auto 0' }} />
            </div>
          )}

          {/* The page — never blank: a loading state (or the streamed card) holds until it is ready. */}
          <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
            style={{ width: '100%', maxWidth: 566, display: 'flex', justifyContent: 'center', ...(paged ? { flex: 1, minHeight: 0 } : null) }}>
            {!bodyReady ? (
              loadingFallback ? <div style={{ width: '100%' }}>{loadingFallback}</div> : (
              <div style={{ padding: '40px 16px', textAlign: 'center', color: '#3c4653', width: '100%', fontFamily: "'Lora', serif" }}>
                <div style={{ fontSize: '1.02rem', marginBottom: 12 }}>Preparing your pages…</div>
                <div style={{ width: 200, height: 4, background: 'rgba(15,23,42,0.07)', borderRadius: 2, margin: '0 auto', overflow: 'hidden' }}>
                  <div style={{ width: '55%', height: '100%', background: 'rgba(15,23,42,0.2)', borderRadius: 2, animation: 'loading 1.5s ease-in-out infinite' }} />
                </div>
              </div>
              )
            ) : paged ? (
              <div data-testid="reveal-body" style={{ width: '100%', height: '100%', position: 'relative' }}
                {...(canExpand ? {
                  role: 'button', tabIndex: 0,
                  'aria-label': 'Open the reader',
                  onClick: () => setExpanded(true),
                  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(true); } },
                  style: { width: '100%', height: '100%', position: 'relative', cursor: 'pointer' },
                } : null)}>
                {/* keyed by page so a turn swaps the page rather than mutating it under the reader */}
                <ReadingCard key={pageIdx} blocks={pageBlocks} isFirstPage={pageIdx === 0} fill />
                {canExpand && (
                  <span data-testid="reveal-open-reader" style={{ position: 'absolute', left: '50%', bottom: 8, transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, background: 'rgba(251,246,236,0.96)', border: '1px solid #eadfc7', color: '#7a6b5a', fontFamily: "'Lora', serif", fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    Tap to read
                  </span>
                )}
              </div>
            ) : useCard ? (
              <div data-testid="reveal-body" className={loadingFallback ? 'reveal-stream-fadein' : ''} style={{ width: '100%' }}>
                <ReadingCard runs={runs || []} isFirstPage={pageIdx === 0} />
              </div>
            ) : (
              <div data-testid="reveal-body" style={{ background: '#faf8f3', border: '1px solid #e4dccb', boxShadow: '0 2px 0 #efe6d2, 0 16px 38px rgba(60,47,30,.12)', overflow: 'hidden' }}>
                <PDFDocument file={pdfUrl} loading="" error="">
                  <PDFPage pageNumber={pageIdx + 1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} loading="" onRenderSuccess={() => { if (onReady) onReady(); }} />
                </PDFDocument>
              </div>
            )}
          </div>

          {/* Pager — hidden when the reveal's pinned dock owns it. */}
          {bodyReady && !hidePager && (
            <div data-testid="reveal-pager" style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14, color: '#6b6154', fontFamily: "'Lora', serif", fontSize: 13 }}>
              <button onClick={() => go(-1)} disabled={atStart} aria-label="Previous page" style={pagerBtn(atStart)}>‹</button>
              {/* founder 2026-08-10: the "your words, just as they read in your book" line is REMOVED */}
              <span style={{ textAlign: 'center', lineHeight: 1.45 }}>Page {pageIdx + 1} of {total}</span>
              <button onClick={() => go(1)} disabled={atEnd} aria-label="Next page" style={pagerBtn(atEnd)}>›</button>
            </div>
          )}

          {/* THE READER. Full screen, its own stage, the same pages. Slim chrome so the page gets
              nearly everything: on a 390x844 phone this is ~740px of page against the ~312px the
              inline stage can spare, which is ~165 words instead of ~51. */}
          {expanded && createPortal(
            <div data-testid="reveal-reader" role="dialog" aria-modal="true" aria-label={chapterTitle || 'Your story'}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: '#FBF6EC', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderBottom: '1px solid #eadfc7' }}>
                <span style={{ fontFamily: "'Lora', serif", fontSize: 14, color: '#7a6b5a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chapterTitle}</span>
                <button data-testid="reveal-reader-close" onClick={() => setExpanded(false)} aria-label="Close the reader"
                  style={{ flexShrink: 0, background: 'none', border: '1px solid #cbbfa8', borderRadius: 999, width: 44, height: 36, cursor: 'pointer', color: '#4c4335', fontSize: 15, fontFamily: "'Lora', serif" }}>✕</button>
              </div>

              <div ref={panelStageRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                style={{ flex: 1, minHeight: 0, padding: '10px 12px' }}>
                <div data-testid="reveal-reader-body" style={{ height: '100%', maxWidth: 566, margin: '0 auto' }}>
                  <ReadingCard key={`r${pageIdx}`} blocks={pageBlocks} isFirstPage={pageIdx === 0} fill />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '8px 14px calc(8px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid #eadfc7', color: '#6b6154', fontFamily: "'Lora', serif", fontSize: 13 }}>
                <button onClick={() => go(-1)} disabled={atStart} aria-label="Previous page" style={pagerBtn(atStart)}>‹</button>
                <span style={{ minWidth: 96, textAlign: 'center' }}>Page {pageIdx + 1} of {total}</span>
                <button onClick={() => go(1)} disabled={atEnd} aria-label="Next page" style={pagerBtn(atEnd)}>›</button>
              </div>
            </div>,
            document.body
          )}

          {/* The measuring host: the REAL card, every block, hidden. Its nodes are what get cloned
              to measure candidate pages, so renderer and measurer cannot disagree. */}
          {paged && allBlocks && cardWidth ? (
            <div ref={measureHostRef} aria-hidden="true"
              // FIXED, not absolute: this host renders EVERY block, so an absolutely-positioned
              // copy extends the document and the reveal starts scrolling for content nobody can
              // see (measured on desktop, 2026-08-10). Fixed is out of flow entirely, and layout
              // still computes, which is all the measurer needs.
              style={{ position: 'fixed', left: -99999, top: 0, width: cardWidth, visibility: 'hidden', pointerEvents: 'none' }}>
              <ReadingCard blocks={allBlocks} isFirstPage />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

const pagerBtn = (disabled) => ({
  border: '1px solid #8F815F', background: '#fff', width: 44, height: 44, borderRadius: '50%',
  cursor: disabled ? 'default' : 'pointer', fontSize: 17, color: '#4c4335', opacity: disabled ? 0.35 : 1,
});
